import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minuti

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const lastLoadedUid = useRef(null);
  const inactivityTimer = useRef(null);
  const loadProfileRef = useRef(null);
  const pendingSessionRef = useRef(null);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setProfileError(null);
    lastLoadedUid.current = null;
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      if (session) logout();
    }, INACTIVITY_TIMEOUT);
  }, [logout, session]);

  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetInactivityTimer));
    resetInactivityTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetInactivityTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivityTimer]);

  const loadProfile = useCallback(async (sess, { force = false } = {}) => {
    const uid = sess?.user?.id;
    if (!uid) {
      lastLoadedUid.current = null;
      setProfile(null);
      return;
    }
    if (!force && lastLoadedUid.current === uid) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, name, role, picture")
        .eq("id", uid)
        .maybeSingle();

      if (error) {
        console.error("[Auth] profile read error:", error);
        setProfileError(error.message || JSON.stringify(error));
      } else {
        setProfileError(null);
      }

      if (data) {
        console.log("[Auth] profile loaded:", data);
        setProfile(data);
        lastLoadedUid.current = uid;
        localStorage.setItem("rubiana-role", data.role || "user");
        return;
      }

      const fallbackName =
        sess.user.user_metadata?.name ||
        (sess.user.email || "").split("@")[0];
      const { data: inserted, error: insertErr } = await supabase
        .from("profiles")
        .insert({ id: uid, email: sess.user.email, name: fallbackName })
        .select("id, email, name, role, picture")
        .maybeSingle();

      if (insertErr) {
        console.error("[Auth] profile self-heal failed:", insertErr);
        setProfileError(insertErr.message || "Impossibile creare profilo");
        setProfile(null);
      } else {
        setProfile(inserted || null);
        lastLoadedUid.current = uid;
      }
    } catch (err) {
      console.error("[Auth] profile load unexpected error:", err);
      setProfileError(err?.message || "Errore caricamento profilo");
    }
  }, []);

  loadProfileRef.current = loadProfile;

  useEffect(() => {
    // Sulla pagina di reset password / accettazione invito, la sessione viene
    // gestita esplicitamente da ResetPassword.tsx (setSession dai token
    // nell'hash). AuthContext non deve interferire qui, altrimenti scatta
    // una race condition tra i due listener onAuthStateChange.
    if (window.location.pathname === "/reset-password") {
      setLoading(false);
      return;
    }

    const { data: subscription } = supabase.auth.onAuthStateChange((event, newSession) => {
      // IMPORTANTE: questo callback NON deve essere async, e non deve fare
      // await su altre chiamate Supabase direttamente al suo interno.
      // Supabase usa un lock interno per gestire questi eventi: se dentro
      // il callback si aspetta un'altra chiamata Supabase (es. una query
      // al database), il lock può bloccarsi indefinitamente — capita
      // soprattutto con l'evento TOKEN_REFRESHED, che scatta quando la tab
      // torna visibile dopo essere stata in background. La soluzione
      // ufficiale è rimandare ("defer") il lavoro asincrono con
      // setTimeout(..., 0), per uscire dal contesto del lock prima di
      // fare altre chiamate.
      if (event === "INITIAL_SESSION") {
        setSession(newSession || null);
        if (newSession) {
          pendingSessionRef.current = newSession;
          setTimeout(() => {
            loadProfileRef.current(newSession);
          }, 0);
        }
        setLoading(false);
      } else if (event === "SIGNED_IN") {
        pendingSessionRef.current = newSession;
        setSession(newSession);
        setTimeout(() => {
          loadProfileRef.current(newSession, { force: true });
        }, 0);
        setLoading(false);
      } else if (event === "SIGNED_OUT") {
        pendingSessionRef.current = null;
        setSession(null);
        lastLoadedUid.current = null;
        setProfile(null);
        setLoading(false);
      } else if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        setSession(newSession);
      }
    });

    // Fallback: se dopo 3 secondi loading è ancora true, usiamo la sessione pendente
    const fallback = setTimeout(async () => {
      if (pendingSessionRef.current) {
        console.warn("[Auth] fallback: using pending session");
        setSession(pendingSessionRef.current);
        await loadProfileRef.current(pendingSessionRef.current);
      }
      setLoading(false);
    }, 3000);

    return () => {
      subscription?.subscription?.unsubscribe?.();
      clearTimeout(fallback);
    };
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setSession(data.session);
    await loadProfile(data.session, { force: true });
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) throw error;
    if (data.session) {
      setSession(data.session);
      await loadProfile(data.session, { force: true });
    }
    return data.user;
  };

  const refreshProfile = () => loadProfile(session, { force: true });

  const user = session?.user
    ? {
        user_id: session.user.id,
        email: session.user.email,
        name: profile?.name || session.user.user_metadata?.name || "",
        role: profile?.role || localStorage.getItem("rubiana-role") || "user",
        picture: profile?.picture,
      }
    : null;

  return (
    <AuthContext.Provider value={{ user, profile, profileError, session, loading, login, register, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
